import { DateInputV2, type IDateInputProps } from "@inputs/date/DateInputV2";
import { useI18nContext } from "@root/utils/organisation";
import { Controller, type FieldPath, type FieldValues, type UseControllerProps } from "react-hook-form";

export type TDateInputFormProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
  TTransformedValues = TFieldValues,
> = Omit<IDateInputProps, "name" | "value" | "onBlur" | "timeZone"> &
  UseControllerProps<TFieldValues, TName, TTransformedValues>;

export const DateInputForm = <
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
  TTransformedValues = TFieldValues,
>({
  name,
  rules,
  shouldUnregister,
  defaultValue,
  control,
  onChange,
  ...props
}: TDateInputFormProps<TFieldValues, TName, TTransformedValues>) => {
  const { timeZone } = useI18nContext();
  return (
    <Controller
      name={name}
      rules={rules}
      shouldUnregister={shouldUnregister}
      defaultValue={defaultValue}
      control={control}
      render={({ field: { value, onChange: onChangeForm, onBlur, ref }, fieldState: { error } }) => (
        <DateInputV2
          {...props}
          timeZone={timeZone}
          value={value}
          onChange={(...args) => {
            if (onChange) {
              onChange(...args);
            }
            onChangeForm(...args);
          }}
          TextFieldProps={{
            ...props.TextFieldProps,
            size: "small",
            onBlur,
            error: Boolean(error),
            helperText: error?.message,
            inputRef: ref,
          }}
        />
      )}
    />
  );
};
